import React, { useEffect, useRef, useState } from 'react';
import { Image, ImageBackground, StyleSheet, useWindowDimensions, View } from 'react-native'
import { useSelector } from 'react-redux'
import { dataConstants } from 'services/redux/data/constants'
import HelperFileCache from 'utils/HelperFileCache'
import HelperSetting from 'utils/helper-setting'
import { settingsConstants } from 'utils/constants/settings-constants'
import * as Animatable from 'react-native-animatable'
import HelperPlatform from 'utils/helper-platform'
import IconWhite from 'assets/icon-white.png';
import BackgroundImage from 'assets/background.png';
import isEqual from 'react-fast-compare'
import { isValidUrl } from 'utils/HelperUrl'
import ThemedVideo from 'components/atoms/ThemedVideo'
import { colorList } from 'utils/colors';

const CACHE_EXTENSIONS = [ 'mp4', 'png', 'jpg', 'jpeg', 'webp' ];
const DEFAULT_MEDIA = { type: 'default', source: IconWhite };

const getSourceUri = source => {
	return typeof source === 'string' ? source : null;
};

const createCacheFilename = ( uri, type ) => {
	const uriWithoutQuery = uri.split(/[?#]/)[ 0 ];
	const extension = HelperFileCache.getExtension( uriWithoutQuery );
	const filename = HelperFileCache.getFilename( uriWithoutQuery );
	const basename = filename.replace( new RegExp(`\\.${ extension }$`), '' );

	return {
		extension,
		filename: HelperFileCache.prepareFilename( `${ basename }___splash.${ extension }` ),
		type,
	};
}

const findCachedMedia = async () => {
	const results = await Promise.all(
		CACHE_EXTENSIONS.map( async extension => {
			return (
				{
					extension,
					files: await HelperFileCache.findFiles( `*___splash.${ extension }` ),
				}
			);
		})
	);

	for( const result of results ) {
		if( result.files.length === 0 ) {
			continue;
		}

		if( result.extension === 'mp4' && HelperPlatform.isWeb() ) {
			continue;
		}

		return {
			type: result.extension === 'mp4' ? 'video' : 'image',
			source: result.files[ 0 ],
		};
	}

	return DEFAULT_MEDIA;
}

const findConfiguredMedia = async () => {
	const videoUrl = HelperSetting.get( settingsConstants.SPLASH_BACKGROUND_VIDEO, null );
	const imageUrl = HelperSetting.get( settingsConstants.SPLASH_BACKGROUND, null );

	// if( !HelperPlatform.isWeb() && isValidUrl( videoUrl ) ) {
	if( isValidUrl( videoUrl ) ) {
		const cached = createCacheFilename( videoUrl, 'video' );

		return {
			type: cached.type,
			source: await HelperFileCache.create( videoUrl, cached.filename ),
		};
	}

	if( isValidUrl( imageUrl ) ) {
		const cached = createCacheFilename( imageUrl, 'image' );

		if( cached.extension !== 'mp4' ) {
			return {
				type: cached.type,
				source: await HelperFileCache.create( imageUrl, cached.filename ),
			};
		}
	}

	return DEFAULT_MEDIA;
}

const SplashBackground = () => {
	const dimensions = useWindowDimensions();

	const settings = useSelector(
		state => {
			return state.data[dataConstants.SETTINGS];
		},
		isEqual
	);

	const [ media, setMedia ] = useState( DEFAULT_MEDIA );
	const mediaRef = useRef( DEFAULT_MEDIA );

	useEffect( () => {
		let cancelled = false;

		const resolveMedia = async () => {
			try {
				const nextMedia = HelperSetting.isReady()
					? await findConfiguredMedia()
					: await findCachedMedia();

				if( cancelled ) {
					return;
				}

				const previousUri = getSourceUri( mediaRef.current.source );
				const nextUri = getSourceUri( nextMedia.source );

				mediaRef.current = nextMedia;
				setMedia( current => {
					return isEqual(current, nextMedia) ? current : nextMedia;
				} );

				if( previousUri?.includes( 'file://' ) && previousUri !== nextUri ) {
					HelperFileCache.deleteFile( previousUri ).catch(() => {
					});
				}
			} catch( error ) {
				if( !cancelled ) {
					mediaRef.current = DEFAULT_MEDIA;
					setMedia( DEFAULT_MEDIA );
				}
			}
		}

		resolveMedia();

		return () => {
			cancelled = true;
		}
	}, [ settings ] );

	let content;

	if( media.type === 'video' ) {
		content = (
			<View style={ styles.mediaFrame }>
				<ThemedVideo
					key={ media.source }
					source={{ uri: media.source }}
					style={[
						styles.video,
						HelperPlatform.select({
							web: null,
							default: {
								width: dimensions.width,
								height: dimensions.height,
							},
						}),
					]}
				/>
			</View>
		);
	} else if( media.type === 'image' ) {
		content = (
			<Image
				style={ styles.image }
				resizeMode="cover"
				fadeDuration={ 0 }
				source={{ uri: media.source }}
			/>
		);
	} else {
		content = (
			<ImageBackground
				source={ BackgroundImage }
				resizeMode="cover"
				imageStyle={ styles.backgroundFill }
				style={ styles.backgroundImage }>

				<Image
					style={[ styles.image, styles.imageDefault ]}
					resizeMode="contain"
					fadeDuration={ 0 }
					source={ IconWhite }
				/>
			</ImageBackground>
		);
	}

	return (
		<Animatable.View
			animation="fadeIn"
			style={ styles.animation }
			duration={ 600 }>

			{ content }
		</Animatable.View>
	);
}

const styles = StyleSheet.create({
	animation: {
		...StyleSheet.absoluteFillObject,
	},
	backgroundImage: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
	},
	backgroundFill: {
		width: '100%',
		height: '100%',
	},
	mediaFrame: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: colorList.PAGE.primary,
	},
	video: {
		flex: 1,
		width: '100%',
		height: '100%',
		zIndex: 1,
		...HelperPlatform.select({
			web: {
				maxWidth: 720,
			}
		}),
	},
	image: {
		width: '100%',
		height: '100%',
	},
	imageDefault: {
		maxWidth: 250,
		maxHeight: 250,
	},
})

export default React.memo( SplashBackground );
